import type { FieldValidator } from '@lucidtech/las-sdk-browser';
import { ColumnDef } from '@tanstack/react-table';
import { memo, useMemo, useRef } from 'react';
import { DialogTrigger } from 'react-aria-components';

import { BasicTooltip, Button, Flex, Heading, Table, TableRef, Text, useComponentStack } from '@/components/core';
import { AddIcon } from '@/components/icons';
import {
  findField,
  NavigationContainer,
  NewValidatorModal,
  ValidatorCell,
  ValidatorConfiguration,
} from '@/components/ui';
import { useModelConfigStore } from '@/store';

export type ValidatorsSectionProps = {
  fieldId: string;
};

const IValidatorsSection = ({ fieldId }: ValidatorsSectionProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const moveValidator = useModelConfigStore((state) => state.moveValidator);
  const deleteValidator = useModelConfigStore((state) => state.deleteValidator);
  const validatorTableRef = useRef<TableRef<FieldValidator>>(null);

  const componentStack = useComponentStack();

  const validatorColumns = useMemo<ColumnDef<FieldValidator>[]>(() => {
    return [
      {
        accessorKey: 'functionId',
        cell: ({ row }) => (
          <ValidatorCell
            validator={row.original}
            onPress={() =>
              componentStack?.push(
                <NavigationContainer>
                  <ValidatorConfiguration fieldId={fieldId} validatorId={row.original.id} />
                </NavigationContainer>
              )
            }
          />
        ),
        size: Number.MAX_SAFE_INTEGER,
      },
    ];
  }, [componentStack, fieldId]);

  const validatorData = useMemo(() => field?.validators ?? [], [field]);
  const hasValidators = validatorData.length > 0;

  if (!field) return null;

  return (
    <section>
      <Flex col className="gap-sm items-start">
        <Flex className="gap-sm w-full items-center pr-[1rem]">
          <Heading size="xs">Validators</Heading>
          <BasicTooltip>
            <Text size="sm">
              Validators let you automatically check that field values are correct — for example, by verifying number
              formats or required fields. Learn more about validators{' '}
              <a href="https://docs.cradl.ai/core-concepts/ai-model/validators" className="text-primary-600">
                here
              </a>
              .
            </Text>
          </BasicTooltip>

          <span className="grow" />

          {hasValidators ? (
            <DialogTrigger>
              <Button className="text-gray-600 hover:bg-gray-50" variant="plain">
                <AddIcon />
              </Button>
              <NewValidatorModal fieldId={fieldId} />
            </DialogTrigger>
          ) : null}
        </Flex>

        <Table
          className="border-separate border-spacing-x-0 border-spacing-y-2 px-4"
          ref={validatorTableRef}
          columns={validatorColumns}
          data={validatorData}
          hideHeader
          onRowIndexChange={(fromIndex, toIndex) => {
            moveValidator(field.id, fromIndex, toIndex, field.parentId);
            validatorTableRef.current?.remountSortableContext();
          }}
          deleteRowAction={(row) => deleteValidator(field.id, row.original.id, field.parentId)}
        />

        {!hasValidators ? (
          <DialogTrigger>
            <Button className="text-gray-600 hover:bg-gray-50" variant="secondary">
              <AddIcon />
              <Text>Add validator</Text>
            </Button>
            <NewValidatorModal fieldId={fieldId} />
          </DialogTrigger>
        ) : null}
      </Flex>
    </section>
  );
};

export const ValidatorsSection = memo(IValidatorsSection) as typeof IValidatorsSection;
